import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { UserModel } from 'src/State/User/UserModel'
import { UserForm } from 'src/Libs/service-dashboard/types/default'
import { CompanyContactData, UserCompanyPayload, User } from '../models'
import fromCompanyDtoToCompanyContactDataModelTransformer from '../transformers/from-company-dto-to-company-contact-data-model.transformer'
import fromUserDtoToUserModelTransformer from '../transformers/from-user-dto-to-model.transfromer'
import { CompanyLocationDto, UsersWithConnectionsDto } from './dto'
import { UserSettings } from 'src/Libs/user/types'
import { objectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers'
import { FetchedUserDataConnected } from '../models/user.model'
import { handleFetchError } from 'src/Libs/api/utils'
import { UserData } from '@e3dc-react/shell/Types'

const errorMessages = {
  couldNotSaveEmail: 'could not send email. Error: ',
}

/**
 * Sends a request to change the email address of a user to a new one
 * @param email
 * @param url page where the email address change will be confirmed (POST request with token will be sent)
 * @param userId
 * @param authFetch
 */
export const changeEmailAddress = async (email: string, userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<string>> => {
  try {
    const response = await authFetch<string>({
      path: `users/${userId}/change-email`,
      method: 'PUT',
      payload: { app: process.env.REACT_APP_NAME, email },
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<string>()
    }
    return new ApiResult<string>(null, true)
  } catch (error) {
    return new ApiResult<string>(null, true)
  }
}

/**
 * Fetch user data by id
 * @param userId
 * @param authFetch
 */
export const fethUserById = async (userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<User>> => {
  try {
    const response = await authFetch<UserModel>({
      path: `users/${userId}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const resultData = fromUserDtoToUserModelTransformer(response.data)

      return new ApiResult<User>(resultData)
    }

    return new ApiResult<User>(null, true)
  } catch (error) {
    return new ApiResult<User>(null, true)
  }
}

/**
 * Fetches user data without transforming to our User model.
 * @deprecated this hook uses UserData for legacy code. New implementation should use our User model.
 */
export const fetchUserDataById = async (userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<UserData>> => {
  try {
    const response = await authFetch<UserData>({
      path: `users/${userId}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<UserData>(response.data)
    }

    return new ApiResult<UserData>(null, true)
  } catch (error) {
    return handleFetchError<UserData>(error)
  }
}

interface FetchUsersParams {
  connect?: string
  filter?: string
  filterOr?: string
  orderBy?: string
  limit?: number
  offset?: number
  fields?: string
}

/**
 * Fetch users data with optional connections
 * @param params
 * @param authFetch
 */
export const fetchUsersWithConnections = async (
  params: FetchUsersParams,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<FetchedUserDataConnected[]>> => {
  try {
    const response = await authFetch<UsersWithConnectionsDto>({
      path: `users`,
      method: 'GET',
      payload: params,
    })

    if (response.status === StatusCodes.OK) {
      const responseAsArray = objectApiResult({ ...response.data })

      return new ApiResult<FetchedUserDataConnected[]>(responseAsArray as FetchedUserDataConnected[])
    }

    return new ApiResult<FetchedUserDataConnected[]>(null, true)
  } catch (error) {
    return handleFetchError<FetchedUserDataConnected[]>(error)
  }
}

/**
 * Fetches user settings
 * @param userId
 * @param authFetch
 * @returns
 */
export const fetchUserSettings = async (userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<UserSettings>> => {
  try {
    const response = await authFetch<UserSettings>({
      path: `users/${userId}/settings`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<UserSettings>(response.data)
    }

    return new ApiResult<UserSettings>(null, true)
  } catch (error) {
    return new ApiResult<UserSettings>(null, true)
  }
}

/**
 * Confirms the email address change
 * @param token token provided by the sent Email address change E-Mail
 * @param userId
 * @param authFetch
 */
export const confirmEmailAddressChange = async (token: string, userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<number>> => {
  try {
    const response = await authFetch<number>({
      path: `users/${userId}/change-email/${token}/confirm`,
      method: 'POST',
    })

    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<number>(response.data)
    }
    return new ApiResult<number>(null, true)
  } catch (error) {
    return new ApiResult<number>(null, true)
  }
}

/**
 * Gets location data for a company
 * @param companyId
 * @param authFetch
 */
export const getInstallationCompanyLocation = async (
  companyId: string | number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<CompanyContactData>> => {
  try {
    const response = await authFetch<CompanyLocationDto>({
      path: `users/companies/${companyId}/location`,
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      const model = fromCompanyDtoToCompanyContactDataModelTransformer(response.data)
      return new ApiResult<CompanyContactData>(model)
    }

    return new ApiResult<CompanyContactData>(null, true)
  } catch (error) {
    return handleFetchError<CompanyContactData>(error)
  }
}

/**
 * Triggers the backend / service-team process to delete user accounts
 * Currently: Backend sends email, service team deletes account manually
 */
export const createRequestToDeleteUserAccount = async (userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/${userId}/delete-request`,
      method: 'POST',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

/**
 * updates user data
 */
export const updateUser = async (userId: number, data: Partial<UserModel>, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/${userId}`,
      method: 'PUT',
      payload: data,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

/**
 * updates company id for given user
 */
export const updateUserCompany = async (userId: number, data: UserCompanyPayload, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/${userId}/company`,
      method: 'PUT',
      payload: data,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const deleteUser = async (userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/${userId}`,
      method: 'DELETE',
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const resendIpinEmail = async (userId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/${userId}/resend-ipin`,
      method: 'POST',
      payload: {},
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const resendInvitationEmail = async (
  userId: number,
  sendIpin: boolean,
  authFetch: AuthFetchWithResponse,
  createdBy?: number
): Promise<ApiResult<void>> => {
  if (!createdBy) {
    return new ApiResult<void>(null, true, new Error('No createdBy provided'))
  }

  try {
    const response = await authFetch<void>({
      path: `users/${userId}/create-password`,
      method: 'POST',
      payload: { app: 'e3dc', sendIpin, createdBy },
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const changeUserLanguage = async (userId: number, lang: AppLang, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `users/${userId}/settings/language`,
      method: 'PUT',
      payload: { lang },
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const getUserLanguage = async (userId: number | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<AppLang>> => {
  if (!userId) {
    return new ApiResult<AppLang>(null, true, new Error('No user id provided'))
  }

  try {
    const response = await authFetch<{ lang: AppLang }>({
      path: `users/${userId}/settings/language`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<AppLang>(response.data.lang, false)
    }

    return new ApiResult<AppLang>(null, true)
  } catch (error) {
    return handleFetchError<AppLang>(error)
  }
}

/**
 * Invite new user to app via service staff.
 */
export const inviteUser = async (
  formValues: UserForm,
  authFetch: AuthFetchWithResponse,
  /** Indicates who invited new user */
  createdBy: number,
  sendIpin = true
): Promise<number> => {
  const userBaseData = {
    username: formValues.username,
    Name: formValues.lastName,
    Vorname: formValues.firstName,
    ipin: formValues.ipin,
    level: formValues.userLevel,
    mailNotification: formValues.eMailNotification,
    isReference: false,
    sapEmployeeID: null,
    ldapEmail: null,
    infinityCertified: formValues.infinityCertified,
    couponPermission: formValues.couponPermission,
    qntmCertified: formValues.qntmCertified,
  }

  const userAddressData = {
    Telefon: formValues.phoneNumber,
    Email: formValues.eMail,
    Land: formValues.country,
    PLZ: formValues.zipCode,
    PlaceOR: formValues.address,
    Address: formValues.city,
  }

  const userCompanyData = {
    companyID: formValues.company,
  }

  try {
    const userResponse = await authFetch<number>({
      path: 'users',
      method: 'POST',
      payload: userBaseData,
    })

    const addressPromise = authFetch({
      path: `users/${userResponse.data}/location`,
      method: 'POST',
      payload: userAddressData,
    })

    const { language } = formValues

    const languagePromise = language && changeUserLanguage(+userResponse.data, language, authFetch)

    const promisesToResolve = [addressPromise, languagePromise]

    if (formValues.company) {
      const companyPromise = authFetch({
        path: `users/${userResponse.data}/company`,
        method: 'PUT',
        payload: userCompanyData,
      })

      promisesToResolve.push(companyPromise)
    }

    try {
      await Promise.all(promisesToResolve)

      try {
        await authFetch({
          path: `users/${userResponse.data}/create-password`,
          method: 'POST',
          payload: { app: 'e3dc', createdBy, sendIpin },
        })

        return userResponse.data
      } catch (error) {
        const axiosError = error as AuthFetchError
        const errorData = axiosError.response?.data

        console.error(errorMessages.couldNotSaveEmail, errorData)
        return Promise.reject(axiosError)
      }
    } catch (error) {
      const axiosError = error as AuthFetchError

      console.error(axiosError)
      return Promise.reject(axiosError)
    }
  } catch (error) {
    const axiosError = error as AuthFetchError

    console.error(axiosError)
    return Promise.reject(axiosError)
  }
}
